import type { TableContent, TableRow } from './table-content.types';

/** A row or section of a Sanity table, holding its own entries in `items`; a cell holds `content`. */
interface TableItem {
  _type?: string;
  title?: string;
  collapsibleOption?: boolean;
  items?: TableItem[];
  content?: unknown;
}

type TableRows = Pick<TableContent, 'headerRows' | 'rows' | 'sections' | 'footerRows'>;

/** Whether the rows come as Sanity stores them, nested in `items`. */
export const isSanityTable = (content: { items?: unknown }): boolean =>
  Array.isArray(content.items) && content.items.length > 0;

const toRow = (item: TableItem): TableRow => ({ columns: (item.items ?? []).map(cell => ({ content: cell.content })) });

/**
 * A table's rows in the shape Sitecore's Table contents resolver sends them. Sanity stores them nested in
 * `items` instead, each entry typed by its kind of row, so they are grouped here; Sitecore's pass through.
 */
export const tableRows = (content: TableRows & { items?: unknown }): TableRows => {
  if (!isSanityTable(content)) {
    return {
      headerRows: content.headerRows,
      rows: content.rows,
      sections: content.sections,
      footerRows: content.footerRows,
    };
  }

  const items = content.items as TableItem[];
  const ofType = (type: string) => items.filter(item => item._type === type);

  return {
    headerRows: ofType('TableHeaderRow').map(toRow),
    rows: ofType('TableRow').map(toRow),
    sections: ofType('TableSection').map(section => ({
      title: section.title,
      collapsible: section.collapsibleOption,
      rows: (section.items ?? []).filter(item => item._type === 'TableRow').map(toRow),
    })),
    footerRows: ofType('TableFooterRow').map(toRow),
  };
};
